import { useMutation, useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import { Input } from "components/base/Form";
import Page from "components/base/Page";
import Spinner, { InlineSpinner } from "components/base/Spinner";
import { PackageStatusChain } from "components/base/Status";
import { Alert } from "components/base/Toast";
import { useModals } from "ModalProvider";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  CREATE_SAMPLE,
  DELETE_SAMPLE,
  UPDATE_SAMPLE,
  FETCH_PACKAGE_INFO,
  SEND_PACKAGE_EMAIL,
} from "./graphql";

import { FaAsterisk } from "react-icons/fa";
import { FiExternalLink } from "react-icons/fi";
import { buildExternalTrackingLink } from ".";
import CcList from "./CcList";
import MessageList, { MessageFormCreate } from "./MessageList";
import PackageImageList from "./PackageImage";

const SERVICE_TYPE_OPTIONS = [
  { value: "express", label: "Express", eta: "3-5 days" },
  { value: "standard", label: "Standard", eta: "5-7 days" },
];

const SERVICE_TYPE_META = SERVICE_TYPE_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option;
  return acc;
}, {});

const getPackageServiceType = (samples = []) => {
  if (!samples.length) return null;
  return samples.some((sample) => sample.serviceType === "express")
    ? "express"
    : "standard";
};

const SampleForm = ({
  packageId,
  ptToolSenders,
  internalUsers,
  isEditable,
  originalSample,
  onSaveDone,
}) => {
  const { present, hide } = useModals();

  const isCreatingNewSample = originalSample ? false : true;
  const [sampleId, setSampleId] = useState(
    originalSample ? originalSample.id : null,
  );
  const [sampleName, setSampleName] = useState(
    originalSample ? originalSample.name : "",
  );
  const [sampleNameCn, setSampleNameCn] = useState(
    originalSample ? originalSample.nameCn || "" : "",
  );
  const [qty, setQty] = useState(originalSample ? originalSample.quantity : 1);
  const [unit, setUnit] = useState(originalSample ? originalSample.unit : "");
  const [material, setMaterial] = useState(
    originalSample ? originalSample.material : "",
  );
  const [materialMark, setMaterialMark] = useState(
    originalSample ? originalSample.materialMark : "",
  );
  const [serviceType, setServiceType] = useState(
    originalSample?.serviceType || "standard",
  );
  const [hsCode, setHsCode] = useState(
    originalSample ? originalSample.hsCode || "" : "",
  );
  const [price, setPrice] = useState(originalSample ? originalSample.price : 0);
  const [messages, setMessages] = useState(
    originalSample
      ? originalSample.checkItems.map((msg) => {
          return {
            id: msg.id,
            senders: msg.senders,
            receivers: msg.receivers,
            content: msg.content,
          };
        })
      : [],
  );

  useEffect(() => {
    if (originalSample) {
      setMessages(
        originalSample.checkItems.map((msg) => {
          return {
            id: msg.id,
            senders: msg.senders,
            receivers: msg.receivers,
            content: msg.content,
          };
        }),
      );
    }
  }, [originalSample]);

  const [isEditing, setIsEditing] = useState(isCreatingNewSample); // focus on new sample
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAddingMsg, setIsAddingMsg] = useState(false);

  const [createSample] = useMutation(CREATE_SAMPLE);
  const [updateSample] = useMutation(UPDATE_SAMPLE);
  const [deleteSample] = useMutation(DELETE_SAMPLE);

  const handleSample = (action) => {
    if (!sampleName) {
      Alert("error", "Name is required");
      return;
    }
    if (!sampleNameCn.trim()) {
      Alert("error", "中文名称必填");
      return;
    }
    if (qty === "" || qty === null || Number.isNaN(Number(qty))) {
      Alert("error", "QTY is required");
      return;
    }
    if (!material || !material.trim()) {
      Alert("error", "材质必填");
      return;
    }
    if (price === "" || price === null || Number.isNaN(Number(price))) {
      Alert("error", "Unit Price is required");
      return;
    }
    if (!serviceType) {
      Alert("error", "Service Type is required");
      return;
    }

    const messagesOnlyUserIds = messages.map((msg) => {
      return {
        id: msg.id,
        senders: msg.senders.map((sender) => sender.id),
        receivers: msg.receivers.map((receiver) => receiver.id),
        content: msg.content,
      };
    });

    if (originalSample) {
      // The reason for not using the isDeleting is beacuse the isDeleting is not updated in time
      if (action === "delete") {
        deleteSample({
          variables: {
            sampleId: sampleId,
          },
          refetchQueries: [
            { query: FETCH_PACKAGE_INFO, variables: { id: Number(packageId) } },
          ],
          onCompleted: () => {
            Alert("success", "Sample deleted successfully");
          },
        });
        hide();
        setIsDeleting(false);
      } else {
        updateSample({
          variables: {
            sampleId: sampleId,
            name: sampleName,
            nameCn: sampleNameCn.trim(),
            quantity: Number(qty),
            unit: unit,
            material: material.trim(),
            materialMark: materialMark,
            serviceType: serviceType,
            hsCode: hsCode,
            price: Number(price),
            messages: messagesOnlyUserIds,
          },
          refetchQueries: [
            { query: FETCH_PACKAGE_INFO, variables: { id: Number(packageId) } },
          ],
          onCompleted: () => {
            Alert("success", "Sample updated successfully");
          },
        });
      }
    } else {
      if (
        messages.length === 0 ||
        messages.some((msg) => msg.senders.length === 0)
      ) {
        Alert("error", "At least one sender is required");
        return;
      }

      createSample({
        variables: {
          name: sampleName,
          nameCn: sampleNameCn.trim(),
          quantity: Number(qty),
          packageId: Number(packageId),
          unit: unit,
          material: material.trim(),
          materialMark: materialMark,
          serviceType: serviceType,
          hsCode: hsCode,
          price: Number(price),
          messages: messagesOnlyUserIds,
        },
        refetchQueries: [
          { query: FETCH_PACKAGE_INFO, variables: { id: Number(packageId) } },
        ],
        onCompleted: (data) => {
          setSampleId(data.createSample.sample.id);
          Alert("success", "Sample added successfully");
        },
      });
    }

    onSaveDone();
    setIsAddingMsg(false);
    setIsEditing(false);
  };

  return (
    <div
      onFocus={() => {
        if (!isEditable) return;

        setIsEditing(true);
      }}
      onBlur={(event) => {
        // Check if the new focused element is still within this div
        if (event.currentTarget.contains(event.relatedTarget)) {
          // If it is, don't exit editing mode
          return;
        }

        if (isEditing && !isDeleting) {
          Alert("error", "Please save your changes!");
        }
      }}
      tabIndex={0} // Make this div focusable
      className={`${isEditable ? "cursor-pointer" : ""} ${isCreatingNewSample ? "border border-dashed dark:border-gray-700" : ""} rounded-lg bg-gray-100 p-6 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900 transition-colors duration-300 ease-in-out`}
      data-testid="sample-form"
    >
      <div className="space-y-2">
        {!isEditing ? (
          <div className="">
            {/* display mode */}
            <div className="space-y-2">
              <div className="mb-4 space-y-1">
                <p className="text-lg font-bold ">{sampleName}</p>
                {sampleNameCn && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {sampleNameCn}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-x-2 gap-y-2">
                <div className="flex items-center">
                  <label className="w-28">QTY: </label>
                  {qty}
                  <span className="ml-2">{unit}</span>
                </div>

                <div className="flex items-center">
                  <label className="w-28">Unit Price: </label>
                  {price} $
                </div>

                <div className="flex items-center">
                  <label className="w-28">材质: </label>
                  <span>{material || "-"}</span>
                  {materialMark ? (
                    <span className="ml-2 text-gray-500">{materialMark}</span>
                  ) : null}
                </div>

                <div className="flex items-center">
                  <label className="w-28">HS Code: </label>
                  {hsCode || "-"}
                </div>

                <div className="col-span-2 flex items-center">
                  <label className="w-28">Service Type: </label>
                  <span
                    className={
                      serviceType === "express"
                        ? "font-semibold text-red-500"
                        : undefined
                    }
                  >
                    {SERVICE_TYPE_META[serviceType]?.label || "Standard"}
                    {" ("}
                    {SERVICE_TYPE_META[serviceType]?.eta || "5-7 days"}
                    {")"}
                  </span>
                </div>
              </div>

              <div className="my-6 border border-gray-300 dark:border-gray-700 border-dashed " />

              <div className="flex">
                <label className="w-28">Message:</label>
              </div>

              <MessageList isEditing={false} messages={messages} />
            </div>
          </div>
        ) : (
          <>
            {/* Editing Mode */}
            <div className="flex items-center">
              <label className="w-28 flex items-center">
                Name:
                <FaAsterisk className="text-red-500 text-xxs ml-1" />
              </label>
              <Input
                placeholder="Sample Name"
                value={sampleName}
                onChange={(e) => setSampleName(e.target.value)}
                className="w-1/2 dark:bg-gray-700 dark:bg-opacity-20"
              />
            </div>

            <div className="flex items-center">
              <label className="w-28 flex items-center">
                中文名称:
                <FaAsterisk className="text-red-500 text-xxs ml-1" />
              </label>
              <Input
                placeholder="中文名称"
                value={sampleNameCn}
                onChange={(e) => setSampleNameCn(e.target.value)}
                className="w-1/2 dark:bg-gray-700 dark:bg-opacity-20"
              />
            </div>

            <div className="flex items-center">
              <label className="w-28 flex items-center">
                QTY:
                <FaAsterisk className="text-red-500 text-xxs ml-1" />
              </label>
              <Input
                type="number"
                min={0}
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                className=" w-14 dark:bg-gray-700 dark:bg-opacity-20"
              />

              <Input
                placeholder="Unit"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                className="w-14 ml-6 dark:bg-gray-700 dark:bg-opacity-20"
              />
              <span className=" text-gray-500 text-opacity-50 ml-2">
                e.g. pair, piece
              </span>
            </div>

            <div className="flex items-center">
              <label className="w-28 flex items-center">
                Unit Price:
                <FaAsterisk className="text-red-500 text-xxs ml-1" />
              </label>
              <Input
                type="number"
                min={0}
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className=" w-14 dark:bg-gray-700 dark:bg-opacity-20"
              />
              <span className="ml-2">$</span>
            </div>

            <div className="flex items-center">
              <label className="w-28 flex items-center">
                材质:
                <FaAsterisk className="text-red-500 text-xxs ml-1" />
              </label>
              <Input
                placeholder="材质"
                value={material}
                onChange={(e) => setMaterial(e.target.value)}
                maxLength={50}
                className="w-40 dark:bg-gray-700 dark:bg-opacity-20"
              />
              <div className="ml-4 flex items-center space-x-4">
                {["带电", "沙子", "木质"].map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() =>
                      setMaterialMark(materialMark === option ? "" : option)
                    }
                    className="flex items-center space-x-1 text-gray-700 dark:text-gray-200"
                  >
                    <span
                      className={`flex h-4 w-4 items-center justify-center rounded-full border ${
                        materialMark === option
                          ? "border-blue-500"
                          : "border-gray-400"
                      }`}
                    >
                      {materialMark === option ? (
                        <span className="h-2 w-2 rounded-full bg-blue-500" />
                      ) : null}
                    </span>
                    <span>{option}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center">
              <label className="w-28 flex items-center">
                Service Type:
                <FaAsterisk className="text-red-500 text-xxs ml-1" />
              </label>
              <div className="flex items-center space-x-4">
                {SERVICE_TYPE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setServiceType(option.value)}
                    className="flex items-center space-x-1 text-gray-700 dark:text-gray-200"
                  >
                    <span
                      className={`flex h-4 w-4 items-center justify-center rounded-full border ${
                        serviceType === option.value
                          ? "border-blue-500"
                          : "border-gray-400"
                      }`}
                    >
                      {serviceType === option.value ? (
                        <span className="h-2 w-2 rounded-full bg-blue-500" />
                      ) : null}
                    </span>
                    <span>{option.label}</span>
                    <span className="text-xs text-gray-500">
                      ({option.eta})
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center">
              <label className="w-28">HS Code:</label>
              <Input
                placeholder="HS Code"
                value={hsCode}
                onChange={(e) => setHsCode(e.target.value)}
                className="w-40 dark:bg-gray-700 dark:bg-opacity-20"
              />
            </div>

            <div className="border border-gray-300 dark:border-gray-700 border-dashed " />

            {isCreatingNewSample ? (
              <>
                <label className="w-28">Message:</label>
                <MessageFormCreate
                  ptToolSenders={ptToolSenders}
                  internalUsers={internalUsers}
                  setMessages={setMessages}
                />
              </>
            ) : (
              <>
                {/* Updating existing sample */}
                {isAddingMsg ? (
                  <div>
                    <label className="w-28">Message:</label>
                    <MessageFormCreate
                      ptToolSenders={ptToolSenders}
                      internalUsers={internalUsers}
                      setMessages={setMessages}
                    />
                  </div>
                ) : (
                  <div className="flex">
                    <label className="w-28">Message:</label>
                    <Button onClick={() => setIsAddingMsg(true)}>
                      + Add Message
                    </Button>
                  </div>
                )}

                <MessageList
                  isEditing={true}
                  messages={messages}
                  ptToolSenders={ptToolSenders}
                  internalUsers={internalUsers}
                  setMessages={setMessages}
                />
              </>
            )}

            <div className="my-4 flex items-center space-x-2">
              <Button
                onClick={handleSample}
                className="h-6 border border-sky-300 text-sky-500 hover:text-sky-800 hover:bg-sky-500 hover:border-sky-500 active:border-sky-700 text-xs px-2 py-1 rounded-md"
              >
                Save
              </Button>

              {!isCreatingNewSample && (
                <Button
                  onClick={() => {
                    setIsDeleting(true);

                    present({
                      title: "Delete Sample",
                      center: true,
                      children: (
                        <div>
                          <p className="py-6">Delete this sample?</p>
                          <div className="flex items-center space-x-4">
                            <Button
                              danger
                              onClick={() => {
                                handleSample("delete");
                              }}
                            >
                              Delete
                            </Button>

                            <Button
                              className=" text-gray-500 hover:text-gray-800"
                              onClick={() => {
                                hide();
                                setIsDeleting(false);
                                setIsEditing(false);
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ),
                    });
                  }}
                  danger={true}
                  className="h-6 border border-red-500 hover:bg-red-500 hover:border-red-500 active:border-red-700 text-xs px-2 py-1 rounded-md"
                >
                  Delete
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const SampleList = ({
  samples,
  packageId,
  ptToolSenders,
  internalUsers,
  isEditable,
}) => {
  const [isCreating, setIsCreating] = useState(false);

  return (
    <div className="text-sm space-y-4">
      {isEditable && !isCreating && (
        <div
          data-testid="package-add-sample"
          onClick={() => setIsCreating(true)}
          className="cursor-pointer text-center p-6 border border-dashed rounded-lg text-sky-500 border-gray-300 hover:text-sky-800 hover:bg-gray-300 dark:border-gray-700 dark:hover:bg-gray-900"
        >
          + Add Sample
        </div>
      )}

      {isCreating && (
        <SampleForm
          packageId={packageId}
          ptToolSenders={ptToolSenders}
          internalUsers={internalUsers}
          isEditable={isEditable}
          onSaveDone={() => {
            setIsCreating(false);
          }}
        />
      )}

      <div data-testid="package-samples" className=" space-y-4">
        {samples.length > 0 ? (
          samples.map((sample) => (
            <SampleForm
              key={sample.id}
              originalSample={sample}
              packageId={packageId}
              ptToolSenders={ptToolSenders}
              internalUsers={internalUsers}
              isEditable={isEditable}
              onSaveDone={() => {
                setIsCreating(false);
              }}
            />
          ))
        ) : (
          <p className="opacity-70 text-xs">No sample added yet.</p>
        )}
      </div>
    </div>
  );
};

const PackageInfo = () => {
  const { packageId } = useParams();
  const { loading, error, data } = useQuery(FETCH_PACKAGE_INFO, {
    variables: { id: Number(packageId) },
    fetchPolicy: "network-only",
  });

  const [sendPackageEmail, sendPackageEmailRes] = useMutation(
    SEND_PACKAGE_EMAIL,
    {
      variables: { id: Number(packageId) },
      onCompleted: (data) => {
        if (data.sendPackageEmail.isSuccess) {
          Alert("success", "Package email sent successfully");
        } else {
          Alert("error", "Failed to send package email");
        }
      },
      onError: (error) => {
        Alert("error", `Failed to send package email: ${error.message}`);
      },
    },
  );

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const isEditable = data.packageInfo.status !== "DELIVERED";
  const canSendPackageEmail = isEditable && !data.packageInfo.hasNotifiedAll;
  const packageServiceType = getPackageServiceType(data.allSamples);

  return (
    <Page
      title={`#${packageId} Package Samples`}
      backTo={`/tools/package`}
      rightButtons={
        <div>
          {canSendPackageEmail && (
            <Button
              onClick={() => sendPackageEmail(Number(packageId))}
              disabled={sendPackageEmailRes.loading}
            >
              {sendPackageEmailRes.loading ? (
                <InlineSpinner size={16} />
              ) : (
                "Email Now"
              )}
            </Button>
          )}
        </div>
      }
    >
      <div className="p-6 grid grid-cols-1 lg:grid-cols-5 gap-x-6 gap-y-6">
        <div
          data-testid="package-flow-status"
          className="lg:col-span-3 text-xs space-y-2 "
        >
          <div className="flex items-center">
            <p className="w-24">Status:</p>
            <PackageStatusChain status={data.packageInfo.status} />
          </div>
          <div className="flex items-center">
            <p className="w-24">Email:</p>
            <p className="font-bold">
              <span
                className={`text-xs rounded-full inline-block text-gray-100 px-4 py-1 scale-90 capitalize whitespace-nowrap ${data.packageInfo.hasNotifiedAll ? "bg-green-500 " : "bg-gray-300 "}`}
              >
                {data.packageInfo.hasNotifiedAll ? "SENT" : "PENDING"}
              </span>
            </p>
          </div>
          <div className="flex items-center">
            <p className="w-24">Destination:</p>
            <p className="font-bold">
              {data.packageInfo.destination.shortName}
            </p>
          </div>
          <div className="flex items-center">
            <p className="w-24">Service Type:</p>
            <p
              className={`font-bold ${packageServiceType === "express" ? "text-red-500" : ""}`}
            >
              {packageServiceType
                ? `${SERVICE_TYPE_META[packageServiceType]?.label} (${SERVICE_TYPE_META[packageServiceType]?.eta})`
                : "-"}
            </p>
          </div>
          <div className="flex items-center">
            <p className="w-24">Tracking NO.:</p>
            <div className="font-bold">
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  const url = buildExternalTrackingLink(data.packageInfo);
                  if (url) window.open(url);
                }}
                className="flex items-center space-x-1 text-sky-600 hover:text-sky-700 active:text-sky-800 font-semibold hover:cursor-pointer"
              >
                <span>{data.packageInfo.trackingNumber}</span>
                <FiExternalLink />
              </div>
            </div>
          </div>
          <div className="flex items-center">
            <p className="w-24 flex-shrink-0">Cc:</p>
            <CcList
              internalUsers={data.allInternalUsers}
              samples={data.allSamples}
              parcel={data.packageInfo}
              isEditable={isEditable}
            />
          </div>
        </div>

        <div className="card lg:col-span-3 self-start">
          <h3 className="mb-2">Samples</h3>
          <SampleList
            samples={data.allSamples}
            packageId={packageId}
            ptToolSenders={data.allPtToolSenders}
            internalUsers={data.allInternalUsers}
            isEditable={isEditable}
          />
        </div>

        <div className="card lg:col-span-2 self-start">
          <h3 className="mb-2">Samples' Images</h3>
          <PackageImageList
            packageId={packageId}
            packageImages={data.allPackageImagesByPkgId}
            isEditable={isEditable}
          />
        </div>
      </div>
    </Page>
  );
};

export default PackageInfo;
